import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/format';
import type { CpmAccountDisqualification } from '@/generated/account-disqualification';

type Props = {
  items: CpmAccountDisqualification[];
  onLift: (item: CpmAccountDisqualification) => void;
  onRestore: (item: CpmAccountDisqualification) => void;
};

const PREFIX = 'admin.campaigns.accountDisqualification';

function statusOf(item: CpmAccountDisqualification): 'active' | 'lifted' | 'restored' {
  if (item.restored_at) return 'restored';
  return item.lifted_at ? 'lifted' : 'active';
}

export function AccountDisqualificationsList({ items, onLift, onRestore }: Props) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <ul className="space-y-2" data-testid="account-disqualifications-list">
      {items.map(item => {
        const status = statusOf(item);
        return (
          <li key={item.id} className="flex flex-wrap items-start justify-between gap-3 rounded-md border bg-muted/20 p-3 text-sm">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{item.account_label}</span>
                <span className="rounded bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
                  {t(`campaign.channels.platformLabel.${item.platform}`, { defaultValue: item.platform })}
                </span>
                <Badge variant={status === 'active' ? 'destructive' : 'outline'} className="text-[10px]">{t(`${PREFIX}.${status}Status`)}</Badge>
              </div>
              <p className="break-words text-muted-foreground">{t(`${PREFIX}.reasonLabel`)}: {item.reason}</p>
              <p className="text-xs text-muted-foreground">
                {item.disqualified_by_name ? `${t(`${PREFIX}.disqualifiedBy`, { name: item.disqualified_by_name })} · ` : ''}{formatDateTime(item.created_at)}
              </p>
              {item.lifted_at && (
                <p className="text-xs text-muted-foreground">
                  {item.lifted_by_name ? `${t(`${PREFIX}.liftedBy`, { name: item.lifted_by_name })} · ` : ''}{formatDateTime(item.lifted_at)}
                </p>
              )}
            </div>
            <div className="flex shrink-0 gap-2">
              {status === 'active' && (
                <Button size="sm" variant="outline" onClick={() => onLift(item)}>{t(`${PREFIX}.liftAction`)}</Button>
              )}
              {item.restorable && item.lifted_at && !item.restored_at && (
                <Button size="sm" variant="outline" onClick={() => onRestore(item)}>{t(`${PREFIX}.restoreAction`)}</Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
